import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './IconSelect.module.css';
import * as FaIcons from 'react-icons/fa';

const IconSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = '',
  disabled = false,
  size = 'md',
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);

  // Encontrar la opción seleccionada
  useEffect(() => {
    const option = options.find(opt => opt.value === value) || options[0];
    setSelectedOption(option);
  }, [value, options]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (option) => {
    if (onChange) {
      onChange(option.value);
    }
    setIsOpen(false);
  };

  const hasLabel = selectedOption?.showLabel !== false;
  
  const containerClasses = [
    styles.container,
    styles[size],
    isOpen && styles.open,
    disabled && styles.disabled,
    !hasLabel && styles.iconOnly,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} ref={containerRef} {...rest}>
      <button
        type="button"
        className={styles.trigger}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selectedOption?.label || placeholder}
      >
        <div className={styles.triggerContent}>
          {selectedOption?.icon && (
            <span className={styles.icon}>
              {selectedOption.icon}
            </span>
          )}
          {selectedOption?.showLabel && (
            <span className={styles.label}>{selectedOption.label}</span>
          )}
        </div>
        <span className={styles.chevron}>
          <FaIcons.FaChevronDown size={12} />
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <ul className={styles.list} role="listbox">
            {options.map((option) => {
              const isSelected = option.value === value;
              return (
                <li
                  key={option.value}
                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(option);
                    }
                  }}
                  tabIndex={0}
                >
                  {option.icon && (
                    <span className={styles.optionIcon}>
                      {option.icon}
                    </span>
                  )}
                  <span className={styles.optionLabel}>{option.label}</span>
                  {isSelected && (
                    <span className={styles.checkmark}>
                      <FaIcons.FaCheck size={12} />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

IconSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      showLabel: PropTypes.bool, // Si se muestra el label en el trigger
    })
  ).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default IconSelect;

